import React, { useEffect, useState } from 'react';
import { TextField, Button, MenuItem, Select, InputLabel, CircularProgress, FormControl, Grid2, Alert, Box, Snackbar } from '@mui/material';
import { jobServices } from '../../services/jobServices';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../services/authentication/authContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { userServices } from '../../services/userServices';
import { useSelector, useDispatch } from 'react-redux';
import { fetchJobStatusData } from '../redux/jobStatusSlice';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import '../../css/Form.css';
import PageHeader from '../ui/PageHeader';
import ConfirmationDialog from '../common/ConfirmationDialog';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const JobForm = () => {

  const [name, setName] = useState('');
  const [clientList, setClientList] = useState([]);
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [createDate, setCreateDate] = useState('');
  const [expectedEndDate, setExpectedEndDate] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [jobId, setJobId] = useState('');
  const [clientName, setClientName] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [allUserData, setAllUserData] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [showBackConfirmation, setShowBackConfirmation] = useState(false);

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { accessToken } = useAuth();
  const navigationUrl = "/home/jobs";
  const currentUserID = localStorage.getItem('userId');
  const location = useLocation();
  const job = location.state?.jobData;
  const id = jobId;
  const jobTypes = [
    { value: "bookkeeping", label: "Bookkeeping" },
    { value: "managementAccounts", label: "Management Accounts" },
    { value: "statutory/FinalAccounts", label: "Statutory/Final Accounts" },
    { value: "vat", label: "VAT" },
    { value: "dataEntry", label: "Data Entry" },
    { value: "payroll", label: "Payroll" },
    { value: "adminWork", label: "Admin Work" },
    { value: "ptr", label: "PTR" },
    { value: "audit", label: "Audit" },
  ];

  const dispatch = useDispatch();
  const { data: jobStatusData } = useSelector(state => state.jobStatus);

  useEffect(() => {
    if (job) {
      setJobId(job.id);
    }
    fetchUserData();
  }, [job]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      userServices.getAllUsersWhichHasNoAdminRole()
        .then((response) => {
          setAllUserData(response);
        })
        .catch((error) => {
          setLoading(false);
        });
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  const addWorkingDays = (startDateStr, workingDaysToAdd) => {
    let date = new Date(startDateStr);
    let addedDays = 0;

    while (addedDays < workingDaysToAdd) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) { // 0 = Sunday, 6 = Saturday
        addedDays++;
      }
    }

    return date.toISOString().split('T')[0];
  };


  useEffect(() => {
    if (accessToken && jobStatusData.length === 0) {
      setLoading(true);
      dispatch(fetchJobStatusData(accessToken)).finally(() => setLoading(false));
    }

    if (accessToken) {
      if (jobId) {
        jobServices.getJobDataFromId(jobId)
          .then((response) => {
            setClientList(response.clientList);
            setName(response.name);
            setType(response.type);
            setCreateDate(new Date(response.createDate.split(' ')[0]).toISOString().split('T')[0]);
            setExpectedEndDate(new Date(response.expectedEndDate.split(' ')[0]).toISOString().split('T')[0]);
            setStatus(response.status);
            setSelectedClientId(response.client.id);
            setAssignedUserId(response.assignedUser.id);
            setClientName(response.client.name);
            setLoading(false);
          })
          .catch((error) => {
            setError(error.message);
            setTimeout(() => {
              setError(null);
            }, 5000);
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    }
  }, [jobId, accessToken, jobStatusData, dispatch]);


  // Fetching client data for the dropdown
  useEffect(() => {
    if (accessToken) {
      jobServices.getClientsDataInJob()
        .then((response) => {
          setClientList(response || []);
        })
        .catch((error) => {
          setError('Error fetching client data: ' + error.message);
          setTimeout(() => {
            setError(null);
          }, 5000);
        });
      setAssignedUserId(currentUserID);
    }
  }, [accessToken, currentUserID]);

  //Add JobStatus As Data requested by default
  useEffect(() => {
    if (!jobId && jobStatusData.length > 0 && !status) {
      setStatus(jobStatusData[0].label);
    }
  }, [jobId, jobStatusData, status]);

  // Handling form submission
  const handleSubmit = (event) => {
    event.preventDefault();

    // const budgetHoursJson = removeDetails(existingData);
    // const budgetHoursStr = String(budgetHours).trim();  

    // if (!budgetHoursStr || budgetHoursStr === '') {
    //   setError('Budget Hours must be set before saving the form.');
    //   setTimeout(() => {
    //     setError(null);
    //   }, 5000);
    //   return;
    // }

    const formData = {
      name,
      clientId: selectedClientId,
      type,
      createDate,
      expectedEndDate,
      status,
      assignedUserId,
    };

    const formUpdate = {
      id,
      name,
      clientId: selectedClientId,
      type,
      createDate,
      expectedEndDate,
      assignedUserId,
      status
    };

    if (accessToken) {
      if (jobId) {
        jobServices.updateJobData(formUpdate)
          .then((updateJob) => {
            localStorage.setItem("successMessage", "Job updated successfully!");
            navigate(navigationUrl);
          })
          .catch((error) => {
            if (error.response?.status === 409) {
              setSnackbarMessage(error.response.data);
            } else if (error.response?.status === 400) {
              setSnackbarMessage(error.response.data);
            } else {
              setSnackbarMessage(error.message);
            }
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          });
      } else {
        jobServices.postJobData(formData)
          .then((newJob) => {
            localStorage.setItem("successMessage", "Job added successfully!");
            navigate(navigationUrl);
            setName('');
            setType('');
            setStatus('');
            setCreateDate('');
            setExpectedEndDate('');
            setSelectedClientId('');
          })
          .catch((error) => {
            if (error.response?.status === 409) {
              setSnackbarMessage(error.response.data);
            } else if (error.response?.status === 400) {
              setSnackbarMessage(error.response.data);
            } else {
              setSnackbarMessage(error.message);
            }
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          });
      }

    }
  };

  const handleCancel = () => {
    handleBack();
  };

  const handleBack = () => {
    if (isDirty) {
      setShowBackConfirmation(true);
    } else {
      navigate(navigationUrl);
    }
  };

  const handleConfirmBack = () => {
    setShowBackConfirmation(false);
    navigate(navigationUrl);
  };


  const handleUserChange = (e) => {
    setAssignedUserId(e.target.value);
    let teamData = allUserData?.find(u => u.id === e.target.value);
    setSelectedUser(teamData);
    if (!loading) {
      setIsDirty(true);
    }
  };

  const handleExpectedEndDate = (e) => {
    const inputEndDate = e.target.value;

    if (createDate > inputEndDate) {
      setError('Error! Create date must be earlier than expected end date.');
      setTimeout(() => setError(null), 4000);
      setExpectedEndDate('');
      return; // Prevent updating the state if the create date is later
    }

    setExpectedEndDate(inputEndDate);
    if (!loading) {
      setIsDirty(true);
    }
  };

  return (
    <Box className="main-form-container">
      <PageHeader 
        title={jobId ? "Edit Job" : "Add Job"} 
        left={
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
        }
      />

      {error && (
        <Alert variant="outlined" severity="error" className="form-message">
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="form" onChange={() => { if (!loading) setIsDirty(true); }}>
        <Grid2 container spacing={2}>
          {/* Job Name */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              label="Job"
              variant="outlined"
              fullWidth
              required
              value={name}
              margin="normal"
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter Job Name"
            />
          </Grid2>

          {/* Client Name (Dropdown with client ID) */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            {jobId ? (
              <TextField
                type="text"
                label="Client"
                variant="outlined"
                fullWidth
                required
                value={clientName}
                margin="normal"
                disabled
              />
            ) : (
              <FormControl fullWidth margin="normal">
                <InputLabel>Client</InputLabel>
                <Select
                  value={(Array.isArray(clientList) && clientList.some((client) => String(client.id) === String(selectedClientId))) ? selectedClientId : ''}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  label="Client"
                  fullWidth
                  required
                >
                  {Array.isArray(clientList) && clientList.length > 0 ? (
                    clientList.map((client) => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>No clients available</MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
          </Grid2>

          {/* Job Type */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>{t('job.job_type')}</InputLabel>
              <Select
                value={(Array.isArray(jobTypes) && jobTypes.some((jt) => jt.label === type || jt.value === type)) ? type : ''}
                required
                onChange={(e) => setType(e.target.value)}
                label={t('job.job_type')}
              >
                {jobTypes.map((jobType) => (
                  <MenuItem key={jobType.value} value={jobType.label}>
                    {jobType.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>

          {/* Job Creation Date */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              type="date"
              label={t('job.create_date')}
              variant="outlined"
              fullWidth
              required
              value={createDate}
              onChange={(e) => {
                const newCreateDate = e.target.value;
                setCreateDate(newCreateDate);
                const calculatedEndDate = addWorkingDays(newCreateDate, 7);
                setExpectedEndDate(calculatedEndDate);
              }}
              margin="normal"
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Grid2>

          {/* Expected End Date */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              type="date"
              label={t('job.end_date')}
              variant="outlined"
              fullWidth
              required
              value={expectedEndDate}
              onChange={(e) => setExpectedEndDate(e.target.value)}
              onBlur={handleExpectedEndDate}
              margin="normal"
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Grid2>

          {/* Job Status */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            {jobId ? (
              <TextField
                type="text"
                label="Job Status"
                variant="outlined"
                fullWidth
                required
                value={status}
                margin="normal"
                disabled
              />
            ) : (
              <FormControl fullWidth margin="normal">
                <InputLabel>Job Status</InputLabel>
                <Select
                  value={(Array.isArray(jobStatusData) && jobStatusData.some((st) => st.label === status || st.value === status)) ? status : ''}
                  onChange={(e) => setStatus(e.target.value)}
                  label={t('job.status')}
                >
                  {jobStatusData && jobStatusData.length > 0 ? (
                    jobStatusData.map((statusItem) => (
                      <MenuItem key={statusItem.value} value={statusItem.label}>
                        {statusItem.label}  {/* This will display the status label */}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>Loading status...</MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
          </Grid2>

          {/* User Selection */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>User</InputLabel>
              <Select
                value={(Array.isArray(allUserData) && allUserData.some((u) => String(u.id) === String(assignedUserId))) ? assignedUserId : ''}
                onChange={handleUserChange}
                label="User"
                required
              >
                {loading ? (
                  <MenuItem value="" disabled>
                    <div sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                    </div>
                  </MenuItem>
                ) : allUserData?.length > 0 ? (
                  allUserData?.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.firstName} {user.middleName} {user.lastName}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    <span sx={{ color: 'text.secondary' }}>No users available</span>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid2>

          {/* User's team details' */}
          {
            selectedUser !== null && <Grid2 size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'rgba(0, 0, 0, 0.23)', // matches default TextField border
                  borderRadius: '0.25rem',
                  padding: '1.0313rem 0.875rem', // matches TextField internal padding
                  marginTop: '1rem', // matches margin="normal"
                  color: 'rgba(0, 0, 0, 0.87)',
                  fontSize: '1rem',
                  lineHeight: '1.4375em',
                  backgroundColor: '#fafafa',
                }}
              >
                {selectedUser?.teamName
                  ? `${selectedUser?.teamName} - ${selectedUser?.teamOwnerId === selectedUser?.id ? 'Team Owner' : 'Team Member'}`
                  : 'No Team found'}
              </Box>

            </Grid2>
          }


          <Grid2 size={{ xs: 12 }} className="form-actions" sx={{ flexDirection: { xs: 'row !important', sm: 'row' }, gap: { xs: 2, sm: 0 }, '& > button': { flex: { xs: 1, sm: 'none' }, margin: { xs: '0 !important', sm: '' } } }}>
            {jobId ?
              (<Button type="submit" variant="contained" className="form-button"
                startIcon={<EditIcon />} >
                {t('job.update')}
              </Button>
              ) : (
                <Button type="submit" variant="contained" className="form-button"
                  startIcon={<SaveIcon />} >
                  {t('job.save')}
                </Button>
              )}
            <Button type="button" variant="contained" color="inherit" onClick={handleCancel} className="form-button-reset"
              startIcon={<CancelIcon />} >
              {t('job.cancel')}
            </Button>
          </Grid2>

        </Grid2>
      </form>
      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ whiteSpace: 'pre-line' }}>
          {Array.isArray(snackbarMessage) ? (
            snackbarMessage.map((msg, index) => (
              <div key={index}>{msg}</div>
            ))
          ) : (
            snackbarMessage
          )}
        </Alert>
      </Snackbar>

      <ConfirmationDialog
        open={showBackConfirmation}
        onClose={() => setShowBackConfirmation(false)}
        onConfirm={handleConfirmBack}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave this page?"
        confirmLabel="Leave Page"
        confirmColor="error"
      />
    </Box>
  );
}

export default JobForm;